import { create } from 'zustand'
import {
  getPipelines,
  getOpportunities,
  getAllContacts,
  syncContact,
  updateOpportunityStage,
  persistModuleStage,
  getConversations,
  getAutomations,
  stageToProgress,
} from '../services/ghlService'

const GHL_CONFIG_KEY = 'ghl_config'

function loadConfig() {
  try {
    const raw = localStorage.getItem(GHL_CONFIG_KEY)
    return raw ? JSON.parse(raw) : { apiKey: '', locationId: '', pipelineId: '' }
  } catch { return { apiKey: '', locationId: '', pipelineId: '' } }
}

const useGhlStore = create((set, get) => ({
  // Config
  config: loadConfig(),
  isConfigured: !!loadConfig().apiKey,

  // Data
  pipelines: [],
  stages: [],
  contacts: [],
  opportunities: [],
  conversations: [],
  automations: [],
  currentOpportunityId: null,
  currentStage: null,
  stageProgress: 10,

  // UI state
  isLoading: false,
  error: null,
  webhookLog: [],

  // ── Config management ──────────────────────────────────────────────────────
  setConfig: (config) => {
    localStorage.setItem(GHL_CONFIG_KEY, JSON.stringify(config))
    if (config.apiKey)     localStorage.setItem('ghl_api_key', config.apiKey)
    if (config.locationId) localStorage.setItem('ghl_location_id', config.locationId)
    if (config.pipelineId) localStorage.setItem('ghl_pipeline_id', config.pipelineId)
    set({ config, isConfigured: !!config.apiKey, error: null })
  },

  clearCredentials: () => {
    localStorage.removeItem(GHL_CONFIG_KEY)
    localStorage.removeItem('ghl_api_key')
    localStorage.removeItem('ghl_location_id')
    localStorage.removeItem('ghl_pipeline_id')
    set({ config: { apiKey: '', locationId: '', pipelineId: '' }, isConfigured: false, contacts: [], opportunities: [], stages: [], conversations: [], automations: [] })
  },

  // ── Load pipelines ─────────────────────────────────────────────────────────
  loadPipelines: async () => {
    set({ isLoading: true, error: null })
    const { data, error } = await getPipelines()
    if (error) {
      set({ isLoading: false, error })
      return
    }
    const pipelines = data?.pipelines || []
    const stages = pipelines[0]?.stages || []
    set({ pipelines, stages, isLoading: false })
  },

  // ── Load contacts ──────────────────────────────────────────────────────────
  loadContacts: async () => {
    set({ isLoading: true, error: null })
    const { data, error } = await getAllContacts()
    if (error) {
      set({ isLoading: false, error })
      return
    }
    set({ contacts: data?.contacts || [], isLoading: false })
  },

  // ── Load opportunities for pipeline ───────────────────────────────────────
  loadOpportunities: async (pipelineId) => {
    set({ isLoading: true, error: null })
    const pid = pipelineId || get().config.pipelineId
    if (!pid) { set({ isLoading: false }); return }
    const { data, error } = await getOpportunities(pid)
    if (error) {
      set({ isLoading: false, error })
      return
    }
    set({ opportunities: data?.opportunities || [], isLoading: false })
  },

  // ── Sync a contact (on lead capture) ─────────────────────────────────────
  syncContactToGhl: async (contactData) => {
    const { data, error } = await syncContact(contactData)
    if (error) {
      console.warn('[GHL] Contact sync failed:', error)
      return null
    }
    get().loadContacts()
    return data?.contact
  },

  // ── Update opportunity stage ──────────────────────────────────────────────
  updateStage: async (stageId, stageName) => {
    const { config, currentOpportunityId } = get()
    if (!config.pipelineId || !currentOpportunityId) {
      set({ currentStage: stageName, stageProgress: stageToProgress(stageName) })
      return
    }
    const { error } = await updateOpportunityStage(config.pipelineId, currentOpportunityId, stageId)
    if (error) console.warn('[GHL] Stage update failed:', error)
    set({ currentStage: stageName, stageProgress: stageToProgress(stageName) })
  },

  // ── Persist module stage to GHL custom field ──────────────────────────────
  persistModuleStageToGhl: async (moduleStage) => {
    const { config, currentOpportunityId } = get()
    if (!config.pipelineId || !currentOpportunityId) return
    await persistModuleStage(config.pipelineId, currentOpportunityId, moduleStage)
  },

  // ── Set current opportunity (deal) ────────────────────────────────────────
  setCurrentOpportunity: (opportunityId, stageName) => {
    set({
      currentOpportunityId: opportunityId,
      currentStage: stageName || 'Lead',
      stageProgress: stageToProgress(stageName || 'Lead'),
    })
  },

  // ── Load conversations for a contact ─────────────────────────────────────
  loadConversations: async (contactId) => {
    const { data, error } = await getConversations(contactId || '')
    if (!error) set({ conversations: data?.conversations || [] })
  },

  // ── Load automations / workflows ─────────────────────────────────────────
  loadAutomations: async () => {
    const { data, error } = await getAutomations()
    if (!error) set({ automations: data?.workflows || [] })
  },

  // ── Webhook event received ────────────────────────────────────────────────
  handleWebhookEvent: (event) => {
    set((state) => ({
      webhookLog: [
        { id: Date.now(), ...event, receivedAt: new Date().toISOString() },
        ...state.webhookLog.slice(0, 49),
      ],
    }))
    if (event.type === 'opportunity.stageChange') {
      set({ currentStage: event.stage, stageProgress: stageToProgress(event.stage) })
    }
    if (event.type === 'contact.created') {
      get().loadContacts()
    }
  },
}))

export default useGhlStore
